import React from 'react';
import { Shield, Clock, DollarSign, FileCheck, CheckCircle2, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const BNRegistrationSection = () => {
  const { t } = useTranslation();
  
  return (
    <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6">
            <Building2 className="w-5 h-5" />
            <span className="font-semibold">{t('bnRegistration.exclusiveBadge')}</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t('bnRegistration.title')}<br />
            <span className="text-yellow-300">{t('bnRegistration.titleHighlight')}</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed" dangerouslySetInnerHTML={{ __html: t('bnRegistration.subtitle1') }} />
          
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed mt-4" dangerouslySetInnerHTML={{ __html: t('bnRegistration.subtitle2') }} />
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Certificado Digital */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20"
          >
            <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mb-6">
              <Shield className="w-8 h-8 text-blue-900" />
            </div>
            <h3 className="text-2xl font-bold mb-4">1️⃣ {t('protectionSystem.digitalCertificateTitle')}</h3>
            <p className="text-blue-100 leading-relaxed">
              Assim que você registra sua obra, o sistema gera automaticamente um <strong>certificado com carimbo de tempo</strong>, hash criptográfico (SHA-256) e assinatura eletrônica válida por lei.
            </p>
          </motion.div>

          {/* Registro BN */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20"
          >
            <div className="w-16 h-16 bg-green-400 rounded-full flex items-center justify-center mb-6">
              <FileCheck className="w-8 h-8 text-blue-900" />
            </div>
            <h3 className="text-2xl font-bold mb-4">2️⃣ Registro Oficial Opcional na BN</h3>
            <p className="text-blue-100 leading-relaxed">
              Mediante <strong>solicitação expressa do autor</strong>, a cada <strong>120 dias</strong> as obras são protocoladas oficialmente na <strong>Biblioteca Nacional</strong>. O autor autoriza que a plataforma figure como <strong>coautora técnica com 1%</strong> (simbólica, apenas para legitimidade formal), garantindo reconhecimento público e arquivamento permanente. A plataforma <strong>renuncia a todos os direitos patrimoniais</strong>, e você mantém <strong>100% dos ganhos econômicos</strong>.
            </p>
          </motion.div>
        </div>

        {/* How it Works */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="bg-white text-gray-900 rounded-2xl p-8 md:p-12"
        >
          <h3 className="text-3xl font-bold mb-8 text-center">{t('protectionSystem.howWorksCollectiveTitle')}</h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h4 className="font-bold text-lg mb-2">Você Registra</h4>
              <p className="text-gray-600">
                Cadastre sua música na plataforma e solicite o registro na BN (opcional e gratuito)
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h4 className="font-bold text-lg mb-2">{t('bnRegistration.weProtocol')}</h4>
              <p className="text-gray-600">
                A cada 120 dias, reunimos todas as obras e enviamos em lote para a Biblioteca Nacional
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h4 className="font-bold text-lg mb-2">Você Recebe</h4>
              <p className="text-gray-600">
                Certificado oficial da BN entregue diretamente na sua conta, sem custo adicional
              </p>
            </div>
          </div>
        </motion.div>

        {/* Advantages */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
            <div>
              <h5 className="font-bold mb-1">{t('bnRegistration.doubleProtection')}</h5>
              <p className="text-sm text-blue-100">Digital + Oficial</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
            <div>
              <h5 className="font-bold mb-1">{t('bnRegistration.automaticProcess')}</h5>
              <p className="text-sm text-blue-100">Sem burocracia</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <DollarSign className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
            <div>
              <h5 className="font-bold mb-1">Custo Zero</h5>
              <p className="text-sm text-blue-100">Incluso no plano</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-blue-300 flex-shrink-0 mt-1" />
            <div>
              <h5 className="font-bold mb-1">Autoria Protegida</h5>
              <p className="text-sm text-blue-100">{t('bnRegistration.privatePublic')}</p>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-xl mb-6 text-blue-100">
            Ao registrar sua música na <strong>Minha Composição</strong>, você recebe proteção imediata e,<br className="hidden md:block" />
            a cada dois meses, ganha <strong className="text-yellow-300">{t('bnRegistration.freeOfficialRegistry')}</strong> junto à Biblioteca Nacional.
          </p>
          <a
            href="/register"
            className="inline-flex items-center gap-2 bg-yellow-400 text-blue-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-yellow-300 transition-all shadow-2xl hover:scale-105"
          >
            🚀 Começar Agora Grátis
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default BNRegistrationSection;
